import { createFileRoute } from "@tanstack/react-router";
import { useCallback } from "react";
import { CircleSpinner } from "react-spinners-kit";

import LangHeading from "@/components/LangHeading";
import ModuleSelector from "@/components/ModuleSelector";
import PageNav from "@/components/PageNav";
import DefaultContentFormatting from "@/features/default-content/DefaultContentFormatting";
import DefaultContentRow from "@/features/default-content/DefaultContentRow";
import Counts from "@/features/editor/Counts";
import { useFilteredIds } from "@/hooks/useFilteredIds";
import { useResetPageOnSearchChange } from "@/hooks/useResetPageOnSearchChange";
import { useTranslations } from "@/queries/useTranslations";
import { paginate } from "@/translationFiltering";

export const Route = createFileRoute("/$version/default-content/")({
    component: DefaultContent,
});

function DefaultContent() {
    const { version } = Route.useParams();
    const search = Route.useSearch();
    const navigate = Route.useNavigate();
    const { data: translations } = useTranslations(version);
    const { filtered, isComparePending } = useFilteredIds(version);

    const pageNr = search.page ?? 1;
    const resetPage = useCallback(() => {
        navigate({
            to: ".",
            search: (prev) => ({ ...prev, page: undefined }),
            replace: true,
        });
    }, [navigate]);

    useResetPageOnSearchChange({ page: pageNr, search, onReset: resetPage });

    if (!translations || !filtered) {
        return <CircleSpinner size={48} color="#666666" />;
    }

    const fullIds = filtered.defaultContent;
    const { ids, pageCount } = paginate(fullIds, pageNr);

    return (
        <>
            <ModuleSelector contentType={"defaultContent"} version={version} />
            <DefaultContentFormatting />
            <Counts currentCount={fullIds.length} totalCount={translations.defaultContent.ids.length} />
            {isComparePending && <div>Loading compare version…</div>}
            <PageNav page={pageNr} maxPage={pageCount} version={version} contentType="default-content" />
            <LangHeading />
            <div>
                {ids.map((id) => (
                    <DefaultContentRow key={id} id={id} version={version} />
                ))}
            </div>
            <PageNav page={pageNr} maxPage={pageCount} version={version} contentType="default-content" />
        </>
    );
}
